import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import LangSwitch from "@/components/LangSwitch";
import ThemeSwitch from "@/components/ThemeSwitch";
import { Button } from "@/components/ui/button";
import { Phone, ArrowLeft } from "lucide-react";
import Marque from "../components/Marque";

const CONTACT = "quotes@roofborder.com";
// Numéro sans frais : « vanity » à l'affichage, chiffres réels dans le lien tel:.
const TEL_AFFICHE = "1-866-4ROOF-21";
const TEL_LIEN = "tel:+18664766321";

// Textes de la politique dans les trois langues (même patron que Tarifs.js).
// Chaque section est un objet { t: titre, blocs: [...] } ; un bloc est
// { p: "paragraphe" }, { ul: ["puce", ...] } ou { note: "encadré en italique" }.
const T = {
  en: {
    retour: "Back to home",
    connexion: "Sign in",
    surtitre: "Legal",
    h1: "Privacy Policy",
    maj: "Last updated: August 1, 2026",
    intro:
      "This policy explains what personal information Roof Border collects, why we collect it, who we share it with and the rights you have over your information.",
    avis:
      "This document is a template. Have it reviewed by a legal advisor before final publication.",
    sections: [
      {
        t: "1. Who we are",
        blocs: [
          {
            p: 'Roof Border ("Roof Border", "we", "us") is a software service, available at roofborder.com, that lets roofing contractors price a roof from satellite imagery and produce branded quotes.',
          },
          {
            p: "The service is operated by Roof Border, 506 rue de la Chapelle, Deux-Montagnes, Quebec, Canada.",
          },
        ],
      },
      {
        t: "2. Information we collect",
        blocs: [
          { p: "Depending on how you use the service, we may collect:" },
          {
            ul: [
              "Roofer account: name, email address, company name, password (stored hashed), contact details and company logo.",
              "Payment: billing is handled by Stripe. We never store your full card numbers.",
              "Estimate data: property addresses you enter and the satellite measurements produced for those roofs.",
              "Widget leads: name, email, phone number and property address submitted by visitors on the websites of our roofer clients.",
              "Technical data: server logs, IP address and session cookies.",
            ],
          },
        ],
      },
      {
        t: "3. Cookies",
        blocs: [
          {
            p: "We use a single strictly necessary session cookie to keep you signed in. It carries no advertising purpose and cannot be declined without making sign-in impossible.",
          },
          {
            p: "We also use two measurement tools, each of which sets a cookie. The Meta (Facebook) pixel tells Meta that a page of roofborder.com was viewed, so we can measure which of our ads bring roofing contractors to us; its cookie expires after 90 days. Google Analytics counts visits and shows which pages get read, where visitors come from and on what kind of device; its cookies expire after two years at most, and Google does not record your IP address.",
          },
          {
            p: "Neither one ever receives your name, email, property address or any quote data. These cookies are set ONLY if you accept them in the banner shown on your first visit — declining changes nothing about how the site works for you, and neither script is loaded at all. You can change your mind at any time through the \"Cookies\" link in the footer. If we ever add another measurement tool, you will be asked again: today's consent covers only the tools named here.",
          },
          {
            p: "Your language and theme preferences, and your answer about cookies, are stored locally in your browser and are not used to track you.",
          },
        ],
      },
      {
        t: "4. Service providers and third parties",
        blocs: [
          {
            p: "To deliver the service we rely on trusted providers who process data on our behalf:",
          },
          {
            ul: [
              "Stripe, for payment processing.",
              "Google Maps Platform and an artificial intelligence service, to measure roofs from satellite imagery.",
              "MongoDB Atlas, for database hosting.",
              "Verpex, for web hosting.",
              "An email delivery service, to send transactional and follow-up emails.",
            ],
          },
          {
            p: "Some of these providers may process data outside Quebec and Canada, including in the United States.",
          },
        ],
      },
      {
        t: "5. Why we use your information",
        blocs: [
          {
            ul: [
              "Provide and maintain the service.",
              "Produce roof measurements and quotes.",
              "Send follow-up emails to leads on behalf of our roofer clients.",
              "Bill you and manage your subscription.",
              "Prevent fraud and abuse.",
            ],
          },
        ],
      },
      {
        t: "6. Widget leads: our role",
        blocs: [
          {
            p: "For leads submitted through the estimate widget, Roof Border acts as a processor on behalf of the roofer, who is the controller of that information.",
          },
          {
            p: "If you are a lead and wish to be removed, use the unsubscribe link in the emails you receive, or contact the roofer directly.",
          },
        ],
      },
      {
        t: "7. Retention, security and your rights",
        blocs: [
          {
            p: "We keep personal information only as long as needed for the purposes above or as required by law.",
          },
          {
            p: "We protect your data with encryption in transit, passwords hashed with bcrypt and restricted access limited to the people who need it.",
          },
          {
            p: "You have the right to access, correct or delete your information and to withdraw your consent. To exercise these rights, write to us at " +
              CONTACT +
              ".",
          },
        ],
      },
      {
        t: "8. Applicable law",
        blocs: [
          {
            p: "We comply with Quebec's Law 25 on the protection of personal information, as well as applicable privacy laws in Canada and the United States.",
          },
          {
            p: "Our person in charge of the protection of personal information can be reached at: Roof Border, quotes@roofborder.com.",
          },
        ],
      },
      {
        t: "9. Changes to this policy",
        blocs: [
          {
            p: "We may update this policy from time to time. Any change takes effect on its publication date, and the date above will be updated accordingly.",
          },
        ],
      },
    ],
  },
  fr: {
    retour: "Retour à l'accueil",
    connexion: "Connexion",
    surtitre: "Mentions légales",
    h1: "Politique de confidentialité",
    maj: "Dernière mise à jour : 1 août 2026",
    intro:
      "Cette politique explique quels renseignements personnels Roof Border recueille, pourquoi nous les recueillons, avec qui nous les partageons et les droits que vous avez sur vos renseignements.",
    avis:
      "Ce document est un modèle. Faites-le réviser par un conseiller juridique avant la mise en ligne définitive.",
    sections: [
      {
        t: "1. Qui nous sommes",
        blocs: [
          {
            p: "Roof Border (« Roof Border », « nous ») est un service logiciel, accessible à roofborder.com, qui permet aux entrepreneurs en toiture d'établir le prix d'un toit à partir d'images satellite et de produire des soumissions à leur image.",
          },
          {
            p: "Le service est exploité par Roof Border, 506 rue de la Chapelle, Deux-Montagnes, Québec, Canada.",
          },
        ],
      },
      {
        t: "2. Renseignements que nous recueillons",
        blocs: [
          { p: "Selon votre usage du service, nous pouvons recueillir :" },
          {
            ul: [
              "Compte couvreur : nom, adresse courriel, nom d'entreprise, mot de passe (stocké haché), coordonnées et logo de l'entreprise.",
              "Paiement : la facturation est traitée par Stripe. Nous ne stockons jamais vos numéros de carte.",
              "Données d'estimation : les adresses de propriétés que vous saisissez et les mesures satellite produites pour ces toits.",
              "Prospects du widget : nom, courriel, numéro de téléphone et adresse de propriété soumis par les visiteurs sur les sites de nos clients couvreurs.",
              "Données techniques : journaux de serveur, adresse IP et cookies de session.",
            ],
          },
        ],
      },
      {
        t: "3. Cookies",
        blocs: [
          {
            p: "Nous utilisons un seul témoin de session strictement nécessaire pour vous garder authentifié. Il n'a aucune finalité publicitaire et ne peut pas être refusé sans rendre la connexion impossible.",
          },
          {
            p: "Nous utilisons aussi deux outils de mesure, qui déposent chacun un témoin. Le pixel de Meta (Facebook) signale à Meta qu'une page de roofborder.com a été consultée, pour mesurer quelles de nos annonces nous amènent des entrepreneurs en toiture ; son témoin expire après 90 jours. Google Analytics compte les visites et indique quelles pages sont lues, d'où viennent les visiteurs et sur quel type d'appareil ; ses témoins expirent après deux ans au plus, et Google n'enregistre pas votre adresse IP.",
          },
          {
            p: "Ni l'un ni l'autre ne reçoit votre nom, votre courriel, votre adresse de propriété ou quelque donnée de soumission que ce soit. Ces témoins ne sont déposés QUE si vous les acceptez dans la bannière affichée à votre première visite — les refuser ne change rien au fonctionnement du site pour vous, et aucun de ces deux scripts n'est alors chargé. Vous pouvez revenir sur votre choix en tout temps par le lien « Témoins » au bas des pages. Si nous ajoutons un jour un autre outil de mesure, la question vous sera reposée : votre accord d'aujourd'hui ne vaut que pour ceux nommés ici.",
          },
          {
            p: "Vos préférences de langue et de thème, ainsi que votre réponse au sujet des témoins, sont stockées localement dans votre navigateur et ne servent pas à vous suivre.",
          },
        ],
      },
      {
        t: "4. Sous-traitants et tiers",
        blocs: [
          {
            p: "Pour fournir le service, nous faisons appel à des fournisseurs de confiance qui traitent des données pour notre compte :",
          },
          {
            ul: [
              "Stripe, pour le traitement des paiements.",
              "Google Maps Platform et un service d'intelligence artificielle, pour mesurer les toits à partir d'images satellite.",
              "MongoDB Atlas, pour l'hébergement de la base de données.",
              "Verpex, pour l'hébergement web.",
              "Un service d'envoi de courriels, pour les courriels transactionnels et de suivi.",
            ],
          },
          {
            p: "Certains de ces fournisseurs peuvent traiter des données hors du Québec et du Canada, notamment aux États-Unis.",
          },
        ],
      },
      {
        t: "5. Finalités de l'utilisation",
        blocs: [
          {
            ul: [
              "Fournir et maintenir le service.",
              "Produire les mesures de toit et les soumissions.",
              "Envoyer les courriels de suivi aux prospects pour le compte de nos clients couvreurs.",
              "Vous facturer et gérer votre abonnement.",
              "Prévenir la fraude et l'abus.",
            ],
          },
        ],
      },
      {
        t: "6. Prospects du widget : notre rôle",
        blocs: [
          {
            p: "Pour les prospects soumis par le widget d'estimation, Roof Border agit comme sous-traitant pour le compte du couvreur, qui est le responsable de ces renseignements.",
          },
          {
            p: "Si vous êtes un prospect et souhaitez vous retirer, utilisez le lien de désabonnement présent dans les courriels reçus, ou communiquez directement avec le couvreur.",
          },
        ],
      },
      {
        t: "7. Conservation, sécurité et vos droits",
        blocs: [
          {
            p: "Nous conservons les renseignements personnels uniquement le temps nécessaire aux finalités ci-dessus ou tel que l'exige la loi.",
          },
          {
            p: "Nous protégeons vos données par le chiffrement en transit, des mots de passe hachés avec bcrypt et un accès restreint aux seules personnes qui en ont besoin.",
          },
          {
            p: "Vous avez le droit d'accéder à vos renseignements, de les rectifier ou de les supprimer, ainsi que de retirer votre consentement. Pour exercer ces droits, écrivez-nous à " +
              CONTACT +
              ".",
          },
        ],
      },
      {
        t: "8. Lois applicables",
        blocs: [
          {
            p: "Nous nous conformons à la Loi 25 du Québec sur la protection des renseignements personnels, ainsi qu'aux lois applicables en matière de vie privée au Canada et aux États-Unis.",
          },
          {
            p: "Notre responsable de la protection des renseignements personnels peut être joint à : Roof Border, quotes@roofborder.com.",
          },
        ],
      },
      {
        t: "9. Modifications de la politique",
        blocs: [
          {
            p: "Nous pouvons modifier cette politique de temps à autre. Toute modification prend effet à sa date de publication, et la date ci-dessus sera mise à jour en conséquence.",
          },
        ],
      },
    ],
  },
  es: {
    retour: "Volver al inicio",
    connexion: "Iniciar sesión",
    surtitre: "Aviso legal",
    h1: "Política de privacidad",
    maj: "Última actualización: 1 de agosto de 2026",
    intro:
      "Esta política explica qué datos personales recopila Roof Border, por qué los recopilamos, con quién los compartimos y los derechos que usted tiene sobre su información.",
    avis:
      "Este documento es un modelo. Hágalo revisar por un asesor legal antes de su publicación definitiva.",
    sections: [
      {
        t: "1. Quiénes somos",
        blocs: [
          {
            p: "Roof Border («Roof Border», «nosotros») es un servicio de software, disponible en roofborder.com, que permite a los contratistas de techado calcular el precio de un techo a partir de imágenes satelitales y producir cotizaciones con su imagen.",
          },
          {
            p: "El servicio es operado por Roof Border, 506 rue de la Chapelle, Deux-Montagnes, Quebec, Canadá.",
          },
        ],
      },
      {
        t: "2. Información que recopilamos",
        blocs: [
          { p: "Según cómo utilice el servicio, podemos recopilar:" },
          {
            ul: [
              "Cuenta de techador: nombre, correo electrónico, nombre de la empresa, contraseña (almacenada de forma cifrada), datos de contacto y logotipo de la empresa.",
              "Pago: la facturación es procesada por Stripe. Nunca almacenamos sus números de tarjeta.",
              "Datos de estimación: las direcciones de propiedades que usted ingresa y las mediciones satelitales producidas para esos techos.",
              "Prospectos del widget: nombre, correo electrónico, número de teléfono y dirección de la propiedad enviados por los visitantes en los sitios de nuestros clientes techadores.",
              "Datos técnicos: registros del servidor, dirección IP y cookies de sesión.",
            ],
          },
        ],
      },
      {
        t: "3. Cookies",
        blocs: [
          {
            p: "Utilizamos una sola cookie de sesión estrictamente necesaria para mantener su sesión iniciada. No tiene ninguna finalidad publicitaria y no puede rechazarse sin impedir el inicio de sesión.",
          },
          {
            p: "También utilizamos dos herramientas de medición, cada una de las cuales coloca una cookie. El píxel de Meta (Facebook) indica a Meta que se consultó una página de roofborder.com, para medir cuáles de nuestros anuncios nos traen contratistas de techos; su cookie expira después de 90 días. Google Analytics cuenta las visitas e indica qué páginas se leen, de dónde vienen los visitantes y en qué tipo de dispositivo; sus cookies expiran después de dos años como máximo, y Google no registra su dirección IP.",
          },
          {
            p: "Ninguna de las dos recibe jamás su nombre, su correo electrónico, su dirección de propiedad ni dato alguno de cotización. Estas cookies se colocan ÚNICAMENTE si usted las acepta en el aviso mostrado en su primera visita — rechazarlas no cambia nada en el funcionamiento del sitio para usted, y ninguno de los dos scripts se carga. Puede cambiar de opinión en cualquier momento mediante el enlace «Cookies» al pie de las páginas. Si algún día añadimos otra herramienta de medición, se le preguntará de nuevo: su consentimiento de hoy solo cubre las herramientas nombradas aquí.",
          },
          {
            p: "Sus preferencias de idioma y de tema, así como su respuesta sobre las cookies, se almacenan localmente en su navegador y no se utilizan para rastrearlo.",
          },
        ],
      },
      {
        t: "4. Proveedores y terceros",
        blocs: [
          {
            p: "Para prestar el servicio recurrimos a proveedores de confianza que tratan datos por cuenta nuestra:",
          },
          {
            ul: [
              "Stripe, para el procesamiento de pagos.",
              "Google Maps Platform y un servicio de inteligencia artificial, para medir los techos a partir de imágenes satelitales.",
              "MongoDB Atlas, para el alojamiento de la base de datos.",
              "Verpex, para el alojamiento web.",
              "Un servicio de envío de correos electrónicos, para los correos transaccionales y de seguimiento.",
            ],
          },
          {
            p: "Algunos de estos proveedores pueden tratar datos fuera de Quebec y de Canadá, incluso en los Estados Unidos.",
          },
        ],
      },
      {
        t: "5. Finalidades del uso",
        blocs: [
          {
            ul: [
              "Prestar y mantener el servicio.",
              "Producir las mediciones de techo y las cotizaciones.",
              "Enviar los correos de seguimiento a los prospectos por cuenta de nuestros clientes techadores.",
              "Facturarle y administrar su suscripción.",
              "Prevenir el fraude y el abuso.",
            ],
          },
        ],
      },
      {
        t: "6. Prospectos del widget: nuestro rol",
        blocs: [
          {
            p: "Para los prospectos enviados mediante el widget de estimación, Roof Border actúa como encargado del tratamiento por cuenta del techador, quien es el responsable de esa información.",
          },
          {
            p: "Si usted es un prospecto y desea darse de baja, utilice el enlace para cancelar la suscripción incluido en los correos que reciba, o comuníquese directamente con el techador.",
          },
        ],
      },
      {
        t: "7. Conservación, seguridad y sus derechos",
        blocs: [
          {
            p: "Conservamos los datos personales únicamente durante el tiempo necesario para las finalidades anteriores o según lo exija la ley.",
          },
          {
            p: "Protegemos sus datos mediante el cifrado en tránsito, contraseñas cifradas con bcrypt y un acceso restringido limitado a las personas que lo necesitan.",
          },
          {
            p: "Usted tiene derecho a acceder a su información, rectificarla o suprimirla, así como a retirar su consentimiento. Para ejercer estos derechos, escríbanos a " +
              CONTACT +
              ".",
          },
        ],
      },
      {
        t: "8. Leyes aplicables",
        blocs: [
          {
            p: "Cumplimos con la Ley 25 de Quebec sobre la protección de los datos personales, así como con las leyes de privacidad aplicables en Canadá y en los Estados Unidos.",
          },
          {
            p: "Nuestro responsable de la protección de los datos personales puede ser contactado en: Roof Border, quotes@roofborder.com.",
          },
        ],
      },
      {
        t: "9. Modificaciones de la política",
        blocs: [
          {
            p: "Podemos modificar esta política de vez en cuando. Cualquier cambio entra en vigor en su fecha de publicación, y la fecha anterior se actualizará en consecuencia.",
          },
        ],
      },
    ],
  },
};

export default function Confidentialite() {
  const { lang } = useI18n();
  const L = T[lang] || T.en;

  return (
    <div className="min-h-screen bg-app text-ink">
      {/* entête — identique à Tarifs.js */}
      <header className="border-b border-line">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Marque taille="md" />
          <div className="flex items-center gap-4">
            <a href={TEL_LIEN} className="hidden md:inline-flex items-center gap-1.5 text-sm font-bold text-brand-ink hover:text-brand transition-colors">
              <Phone className="h-4 w-4" /> {TEL_AFFICHE}
            </a>
            <ThemeSwitch />
            <LangSwitch langues={["en", "fr", "es"]} />
            <Link to="/">
              <Button variant="outline" className="border-line-strong rounded-md hover:bg-hover-strong hover:text-ink h-9">
                <ArrowLeft className="h-4 w-4 mr-1.5" /> {L.retour}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-ink mb-3">{L.surtitre}</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter mb-3">
          {L.h1}
        </h1>
        <p className="text-sm text-ink-faint mb-6">{L.maj}</p>
        <p className="text-ink-soft leading-relaxed mb-6">{L.intro}</p>

        {/* avis juridique : encadré visible en italique */}
        <p className="rounded-lg border border-line-strong bg-surface px-4 py-3 text-sm italic text-ink-soft mb-12">
          {L.avis}
        </p>

        {/* sections en prose */}
        <div className="space-y-10">
          {L.sections.map((sec, i) => (
            <section key={i}>
              <h2 className="font-heading font-bold text-2xl tracking-tight mb-4">{sec.t}</h2>
              <div className="space-y-4">
                {sec.blocs.map((b, j) => {
                  if (b.ul) {
                    return (
                      <ul key={j} className="space-y-2 pl-5 list-disc marker:text-brand-ink text-ink-soft leading-relaxed">
                        {b.ul.map((li, k) => (
                          <li key={k}>{li}</li>
                        ))}
                      </ul>
                    );
                  }
                  if (b.note) {
                    // Note de complétion : en italique, visuellement distincte.
                    return (
                      <p key={j} className="rounded-lg border border-line bg-surface px-4 py-3 text-sm italic text-ink-faint">
                        {b.note}
                      </p>
                    );
                  }
                  return (
                    <p key={j} className="text-ink-soft leading-relaxed">
                      {b.p}
                    </p>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </main>

      {/* pied de page — identique à Tarifs (Marque + courriel + téléphone) */}
      <footer className="border-t border-line">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <Marque taille="sm" />
          </div>
          <div className="flex flex-col items-start sm:items-end gap-1">
            <a href={TEL_LIEN} className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-ink hover:underline">
              <Phone className="h-4 w-4" /> {TEL_AFFICHE}
            </a>
            <a href={`mailto:${CONTACT}`} className="text-sm text-brand-ink hover:underline">
              {CONTACT}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
